import { Check, Loader2, Trash2, X } from "lucide-react";
import { Button } from "../../../ui/common/button";
import { Tooltip, TooltipTrigger, TooltipContent } from "../../../ui/common/tooltip";

interface InlineEditActionsProps {
  isSaving: boolean;
  saveLabel: string;
  cancelLabel: string;
  onSave: () => void;
  onCancel: () => void;
  /** Ghost inside the version-name cell, solid/outline in the details block. */
  variant?: "ghost" | "bordered";
  /** Matches the sibling input's own height: h-8 next to the h-8 rename input, h-10 next to the
   * default-height inputs in the version-details form. Mismatched heights read as misaligned
   * even when the row is vertically centered. */
  size?: "sm" | "md";
  /** Omit to hide the delete button entirely -- callers only pass it when there is saved data to clear. */
  onDelete?: () => void;
  deleteLabel?: string;
}

/**
 * `tw-size-*`, not separate `tw-h-* tw-w-*`: the Button component's `size="icon"` variant is
 * itself `tw-size-9`, and tailwind-merge does not treat `size-*` as conflicting with `h-*`/`w-*`
 * (verified: `twMerge("size-9 h-10 w-10")` leaves all three classes in place). With two
 * same-specificity rules left in the sheet, which one visually wins is down to Tailwind's
 * generated CSS order, not JSX prop order -- so the override silently failed. Matching the same
 * `size-*` utility the variant uses is what lets tailwind-merge actually dedupe it.
 */
const SIZE_CLASSES: Record<"sm" | "md", { button: string; icon: string }> = {
  sm: { button: "tw-size-8", icon: "tw-h-3.5 tw-w-3.5" },
  md: { button: "tw-size-10", icon: "tw-h-4 tw-w-4" }
};

/**
 * The Save (check) / Cancel (X) pair shared by every inline editor in this feature -- the name
 * cell and the version-details block. Presentational only: it never knows what is being saved.
 */
export const InlineEditActions = ({
  isSaving,
  saveLabel,
  cancelLabel,
  onSave,
  onCancel,
  variant = "ghost",
  size = "sm",
  onDelete,
  deleteLabel
}: InlineEditActionsProps) => {
  const { button: buttonClass, icon: iconClass } = SIZE_CLASSES[size];

  return (
    <div className="tw-flex tw-items-center tw-gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            size="icon"
            variant={variant === "ghost" ? "ghost" : "default"}
            className={`${buttonClass} tw-shrink-0`}
            aria-label={saveLabel}
            disabled={isSaving}
            onClick={onSave}
          >
            {isSaving ? (
              <Loader2 className={`${iconClass} tw-animate-spin`} />
            ) : (
              <Check className={iconClass} />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{saveLabel}</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            size="icon"
            variant={variant === "ghost" ? "ghost" : "outline"}
            className={`${buttonClass} tw-shrink-0`}
            aria-label={cancelLabel}
            disabled={isSaving}
            onClick={onCancel}
          >
            <X className={iconClass} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{cancelLabel}</TooltipContent>
      </Tooltip>
      {onDelete ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              size="icon"
              variant={variant === "ghost" ? "ghost" : "outline"}
              className={`${buttonClass} tw-shrink-0 tw-text-destructive hover:tw-text-destructive`}
              aria-label={deleteLabel}
              disabled={isSaving}
              onClick={onDelete}
            >
              <Trash2 className={iconClass} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{deleteLabel}</TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  );
};
